import { useEffect, useState } from 'react';

/** How often the elapsed clock repaints while something is in flight. */
const TICK_MS = 1_000;

/**
 * Milliseconds elapsed since `active` became true; `0` whenever it is false.
 *
 * Exists so a wait can be described HONESTLY (E-SF18 §5.3): the sign step's
 * progress copy escalates with elapsed time rather than sitting on one unchanging
 * "Loading…", which over a render that can run for tens of seconds is
 * indistinguishable from a hung page.
 *
 * ── IT RECOMPUTES FROM AN ABSOLUTE INSTANT, LIKE `useResendCooldown` ───────
 * The interval is only a REPAINT trigger; the value is always
 * `Date.now() - startedAt`. A background tab whose timers are throttled or dropped
 * therefore reports the true elapsed time the moment it is foregrounded, instead
 * of an accumulator that quietly under-counts by however long the tab was asleep —
 * which would tell a customer who came back after two minutes that their document
 * had been loading for four seconds.
 *
 * Resets to 0 on each new activation so a retry is timed from the retry, not from
 * the first attempt.
 */
export function useElapsedMs(active: boolean): number {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!active) {
      setElapsedMs(0);
      return undefined;
    }
    const startedAt = Date.now();
    setElapsedMs(0);
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, TICK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [active]);

  return elapsedMs;
}
